import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { AuthContext } from '../App';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Send, Mic, Square, X, Play, Pause, ImagePlus, ArrowRight, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useOnlineIds } from './OnlineUsers';
import { playMessageChime, vibrate } from '../utils/chime';

const API = process.env.REACT_APP_BACKEND_URL || '';

const MAX_RECORD_SEC = 60;

// נגן הודעה קולית בתוך בועה
const VoiceBubblePlayer = ({ audioData, duration, mine }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); } else { a.play(); }
  };

  const fmt = (s) => {
    if (!s || isNaN(s)) return '0:00';
    return `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
  };

  return (
    <div className="flex items-center gap-2 min-w-[180px]" data-testid="voice-player">
      <audio
        ref={audioRef}
        src={audioData}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => { setPlaying(false); setProgress(0); }}
        onTimeUpdate={(e) => {
          const a = e.target;
          const d = a.duration && isFinite(a.duration) ? a.duration : duration;
          if (d) setProgress((a.currentTime / d) * 100);
        }}
      />
      <button
        onClick={toggle}
        className={`w-9 h-9 shrink-0 rounded-full flex items-center justify-center ${mine ? 'bg-white/25 text-white' : 'bg-emerald-500 text-white'}`}
        data-testid="voice-play-btn"
      >
        {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 mr-[-2px]" />}
      </button>
      <div className="flex-1">
        <div className={`h-1.5 rounded-full overflow-hidden ${mine ? 'bg-white/25' : 'bg-slate-200'}`}>
          <div className={`h-full rounded-full transition-[width] duration-200 ${mine ? 'bg-white' : 'bg-emerald-500'}`} style={{ width: `${progress}%` }} />
        </div>
      </div>
      <span className={`text-[11px] shrink-0 ${mine ? 'text-emerald-100' : 'text-slate-400'}`}>🎤 {fmt(duration)}</span>
    </div>
  );
};

const ChatConversation = ({ open, onClose, otherUserId, otherUserName, onActivity }) => {
  const { user } = useContext(AuthContext);
  const [messages, setMessages] = useState([]);
  const [otherUser, setOtherUser] = useState(null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recordSec, setRecordSec] = useState(0);
  const [lightboxImg, setLightboxImg] = useState(null);
  const fileInputRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const cancelledRef = useRef(false);
  const timerRef = useRef(null);
  const bottomRef = useRef(null);
  const firstLoadRef = useRef(true);

  const token = localStorage.getItem('token') || sessionStorage.getItem('token');

  const [deleteTarget, setDeleteTarget] = useState(null);

  const deleteMessage = async (messageId, mode) => {
    try {
      await axios.delete(`${API}/api/messages/${messageId}?mode=${mode}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessages((prev) => prev.filter((m) => m.id !== messageId));
      setDeleteTarget(null);
      toast.success(mode === 'me' ? 'ההודעה נמחקה אצלך' : 'ההודעה נמחקה אצל כולם');
    } catch {
      toast.error('שגיאה במחיקת ההודעה');
    }
  };

  const fetchConversation = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/messages/conversation/${otherUserId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = res.data.messages || [];
      setMessages((prev) => {
        const prevIncoming = prev.filter((m) => m.sender_id === otherUserId).length;
        const newIncoming = list.filter((m) => m.sender_id === otherUserId).length;
        if (prev.length > 0 && newIncoming > prevIncoming) {
          playMessageChime();
          vibrate();
        }
        return list;
      });
      setOtherUser(res.data.other_user);
    } catch (e) {
      console.error(e);
    }
  }, [otherUserId, token]);

  useEffect(() => {
    if (!open || !otherUserId) return;
    firstLoadRef.current = true;
    fetchConversation();
    const interval = setInterval(fetchConversation, 5000);
    return () => clearInterval(interval);
  }, [open, otherUserId, fetchConversation]);

  useEffect(() => {
    if (!open) return;
    bottomRef.current?.scrollIntoView({ behavior: firstLoadRef.current ? 'auto' : 'smooth' });
    if (messages.length > 0) firstLoadRef.current = false;
  }, [messages, open]);

  const stopTimer = () => { if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'].find((m) => window.MediaRecorder && MediaRecorder.isTypeSupported(m)) || '';
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime, audioBitsPerSecond: 32000 } : undefined);
      chunksRef.current = [];
      cancelledRef.current = false;
      rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        stopTimer();
        const secs = recordSecRef.current;
        setRecording(false);
        setRecordSec(0);
        if (cancelledRef.current || chunksRef.current.length === 0 || secs < 1) return;
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' });
        const base64 = await new Promise((resolve) => {
          const r = new FileReader();
          r.onloadend = () => resolve(r.result);
          r.readAsDataURL(blob);
        });
        await sendVoice(base64, secs);
      };
      recorderRef.current = rec;
      rec.start(250);
      setRecording(true);
      setRecordSec(0);
      recordSecRef.current = 0;
      timerRef.current = setInterval(() => {
        recordSecRef.current += 1;
        setRecordSec(recordSecRef.current);
        if (recordSecRef.current >= MAX_RECORD_SEC) {
          recorderRef.current?.stop();
        }
      }, 1000);
    } catch (e) {
      toast.error('אין גישה למיקרופון - אשר הרשאה בדפדפן');
    }
  };
  const recordSecRef = useRef(0);

  const stopRecording = (cancel = false) => {
    cancelledRef.current = cancel;
    recorderRef.current?.stop();
  };

  const compressImage = (file) => new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const MAX = 1280;
      let { width, height } = img;
      if (width > MAX || height > MAX) {
        const scale = MAX / Math.max(width, height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL('image/jpeg', 0.8));
    };
    img.onerror = reject;
    img.src = url;
  });

  const handleImagePick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('יש לבחור קובץ תמונה'); return; }
    setSending(true);
    try {
      const dataUrl = await compressImage(file);
      if (dataUrl.length > 4_000_000) { toast.error('התמונה גדולה מדי'); return; }
      await axios.post(
        `${API}/api/messages/send`,
        { recipient_id: otherUserId, subject: '💬 צ׳אט', content: '📷 תמונה', message_type: 'image', image_data: dataUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchConversation();
      if (onActivity) onActivity();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'שגיאה בשליחת התמונה');
    } finally {
      setSending(false);
    }
  };

  const sendVoice = async (audioData, duration) => {
    setSending(true);
    try {
      await axios.post(
        `${API}/api/messages/send`,
        { recipient_id: otherUserId, subject: '💬 צ׳אט', content: '🎤 הודעה קולית', message_type: 'voice', audio_data: audioData, duration },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchConversation();
      if (onActivity) onActivity();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'שגיאה בשליחת ההקלטה');
    } finally {
      setSending(false);
    }
  };

  const handleSend = async () => {
    const content = text.trim();
    if (!content || sending) return;
    setSending(true);
    try {
      await axios.post(
        `${API}/api/messages/send`,
        { recipient_id: otherUserId, subject: '💬 צ׳אט', content },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setText('');
      await fetchConversation();
      if (onActivity) onActivity();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'שגיאה בשליחת ההודעה');
    } finally {
      setSending(false);
    }
  };

  const fmtTime = (iso) => {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const today = new Date().toDateString() === d.toDateString();
    return today
      ? d.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })
      : d.toLocaleDateString('he-IL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  const displayName = otherUser?.full_name || otherUserName || '';
  const onlineIds = useOnlineIds();
  const isOnline = onlineIds.has(otherUserId);
  const [lastSeen, setLastSeen] = useState(null);

  useEffect(() => {
    if (!open || isOnline || !otherUserId) {
      setLastSeen(null);
      return;
    }
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    axios.get(`${API}/api/presence/last-seen/${otherUserId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => setLastSeen(res.data?.last_seen || null))
      .catch(() => setLastSeen(null));
  }, [open, isOnline, otherUserId]);

  const formatLastSeen = (iso) => {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    const diffMin = Math.floor((Date.now() - d.getTime()) / 60000);
    if (diffMin < 60) return `נראה לאחרונה לפני ${Math.max(diffMin, 2)} דקות`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `נראה לאחרונה לפני ${diffHr === 1 ? 'שעה' : `${diffHr} שעות`}`;
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    if (d.toDateString() === yesterday.toDateString()) {
      return `נראה לאחרונה אתמול ב-${d.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}`;
    }
    return `נראה לאחרונה ב-${d.toLocaleDateString('he-IL', { day: 'numeric', month: 'short' })}`;
  };

  const subtitle = isOnline
    ? 'מחובר עכשיו · שיחה פרטית'
    : (lastSeen && formatLastSeen(lastSeen)) || 'שיחה פרטית · מתעדכן אוטומטית';

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        className="p-0 overflow-hidden flex flex-col gap-0 chat-fullscreen-mobile sm:max-w-[560px] [&>button]:hidden"
        style={{ height: 'min(80vh, 640px)' }}
        dir="rtl"
        data-testid="chat-dialog"
      >
        {/* Header */}
        <div
          className="bg-gradient-to-l from-blue-600 via-cyan-500 to-emerald-500 px-3 sm:px-5 py-3 sm:py-4 text-white flex items-center gap-2.5 shrink-0"
          style={{ paddingTop: 'max(env(safe-area-inset-top), 0.75rem)' }}
        >
          <button
            onClick={onClose}
            className="shrink-0 p-1.5 rounded-full hover:bg-white/20 active:scale-95 transition-all"
            aria-label="חזרה"
            data-testid="chat-back-btn"
          >
            <ArrowRight className="w-6 h-6" />
          </button>
          <Link to={`/profile/${otherUserId}`} className="shrink-0">
            <Avatar className="w-11 h-11 border-2 border-white/50">
              {otherUser?.avatar && <AvatarImage src={otherUser.avatar} alt={displayName} />}
              <AvatarFallback className="bg-white/20 text-white font-bold">{(displayName || '?').charAt(0)}</AvatarFallback>
            </Avatar>
          </Link>
          <div className="min-w-0">
            <DialogTitle className="text-lg font-black text-white truncate flex items-center gap-2">
              {displayName}
              {isOnline && (
                <span className="inline-flex items-center gap-1 bg-white/20 text-white text-[10px] font-bold px-2 py-0.5 rounded-full" data-testid="chat-online-badge">
                  <span className="w-2 h-2 bg-[#3ddfa9] rounded-full animate-pulse"></span>
                  מחובר
                </span>
              )}
            </DialogTitle>
            <DialogDescription className="text-white/75 text-xs" data-testid="chat-subtitle">
              {subtitle}
            </DialogDescription>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-4 bg-slate-50 space-y-2" data-testid="chat-messages">
          {messages.length === 0 && (
            <p className="text-center text-sm text-gray-400 mt-8">אין הודעות עדיין — תתחילו לדבר 🎾</p>
          )}
          {messages.map((m) => {
            const mine = m.sender_id === user?.id;
            return (
              <div key={m.id} className={`flex items-center gap-1 group/msg ${mine ? 'justify-start' : 'justify-end'}`}>
                {mine && (
                  <button
                    onClick={() => setDeleteTarget(m)}
                    className="shrink-0 p-1.5 rounded-full text-slate-300 hover:text-red-500 hover:bg-red-50 opacity-60 sm:opacity-0 sm:group-hover/msg:opacity-100 focus:opacity-100 transition-opacity"
                    title="מחק הודעה"
                    aria-label="מחק הודעה"
                    data-testid={`delete-msg-${m.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
                <div
                  className={`max-w-[78%] rounded-2xl px-4 py-2.5 shadow-sm ${
                    mine
                      ? 'bg-emerald-500 text-white rounded-bl-md'
                      : 'bg-white border border-slate-200 text-slate-800 rounded-br-md'
                  }`}
                  data-testid={`chat-bubble-${m.id}`}
                >
                  {m.message_type === 'voice' && m.audio_data ? (
                    <VoiceBubblePlayer audioData={m.audio_data} duration={m.duration} mine={mine} />
                  ) : m.message_type === 'image' && m.image_data ? (
                    <img
                      src={m.image_data}
                      alt="תמונה"
                      onClick={() => setLightboxImg(m.image_data)}
                      className="rounded-xl max-h-56 max-w-full cursor-zoom-in"
                      data-testid={`image-bubble-${m.id}`}
                    />
                  ) : (
                    <>
                      {m.subject && !m.subject.includes('צ׳אט') && (
                        <p className={`text-xs font-bold mb-0.5 ${mine ? 'text-emerald-100' : 'text-slate-400'}`}>{m.subject}</p>
                      )}
                      <p className="whitespace-pre-wrap text-sm leading-relaxed">{m.content}</p>
                    </>
                  )}
                  <p className={`text-[10px] mt-1 ${mine ? 'text-emerald-100/80' : 'text-slate-400'}`}>{fmtTime(m.created_at)}</p>
                </div>
                {!mine && (
                  <button
                    onClick={() => setDeleteTarget(m)}
                    className="shrink-0 p-1.5 rounded-full text-slate-300 hover:text-red-500 hover:bg-red-50 opacity-60 sm:opacity-0 sm:group-hover/msg:opacity-100 focus:opacity-100 transition-opacity"
                    title="מחק הודעה"
                    aria-label="מחק הודעה"
                    data-testid={`delete-msg-${m.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>

        {/* Delete message chooser (WhatsApp style) */}
        {deleteTarget && (
          <div
            className="absolute inset-0 z-50 bg-black/40 flex items-center justify-center p-6"
            onClick={() => setDeleteTarget(null)}
          >
            <div
              className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl p-4 w-full max-w-xs"
              onClick={(e) => e.stopPropagation()}
              data-testid="delete-msg-dialog"
            >
              <p className="font-bold text-slate-800 dark:text-slate-100 mb-3 text-center">למחוק את ההודעה?</p>
              <div className="space-y-2">
                <button
                  onClick={() => deleteMessage(deleteTarget.id, 'me')}
                  data-testid="delete-for-me-btn"
                  className="w-full py-2.5 rounded-xl bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 font-bold text-slate-700 dark:text-slate-200 text-sm transition-colors"
                >
                  מחק אצלי בלבד
                </button>
                {deleteTarget.sender_id === user?.id && (
                  <button
                    onClick={() => deleteMessage(deleteTarget.id, 'all')}
                    data-testid="delete-for-all-btn"
                    className="w-full py-2.5 rounded-xl bg-red-500 hover:bg-red-600 text-white font-bold text-sm transition-colors"
                  >
                    מחק אצל כולם
                  </button>
                )}
                <button
                  onClick={() => setDeleteTarget(null)}
                  className="w-full py-2.5 rounded-xl text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800 font-bold text-sm transition-colors"
                >
                  ביטול
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Input */}
        <div className="border-t border-slate-200 bg-white px-3 py-3 flex items-end gap-2 shrink-0" style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 0.75rem)' }}>
          {recording ? (
            <div className="flex-1 flex items-center gap-3 bg-red-50 border border-red-200 rounded-2xl px-4 py-2.5" data-testid="recording-bar">
              <span className="w-3 h-3 rounded-full bg-red-500 animate-pulse shrink-0" />
              <span className="text-red-600 font-bold text-sm">מקליט... {Math.floor(recordSec / 60)}:{String(recordSec % 60).padStart(2, '0')}</span>
              <span className="text-xs text-red-400">(מקס׳ 1:00)</span>
              <div className="flex-1" />
              <button
                onClick={() => stopRecording(true)}
                className="w-9 h-9 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center hover:bg-slate-300"
                title="ביטול"
                data-testid="record-cancel-btn"
              >
                <X className="w-4 h-4" />
              </button>
              <button
                onClick={() => stopRecording(false)}
                className="w-9 h-9 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600"
                title="עצור ושלח"
                data-testid="record-stop-send-btn"
              >
                <Square className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                placeholder="כתוב הודעה..."
                rows={1}
                className="flex-1 resize-none rounded-2xl border border-slate-300 px-4 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-emerald-400 max-h-28"
                style={{ minHeight: '44px', fontSize: '16px' }}
                data-testid="chat-input"
              />
              <button
                onClick={handleSend}
                disabled={sending || !text.trim()}
                className={`w-11 h-11 shrink-0 rounded-full flex items-center justify-center transition-colors ${
                  text.trim()
                    ? 'bg-emerald-500 text-white hover:bg-emerald-600'
                    : 'bg-slate-200 text-slate-400'
                } disabled:opacity-60`}
                aria-label="שלח הודעה"
                title="שלח הודעה"
                data-testid="chat-send-btn"
              >
                <Send className="w-5 h-5 -scale-x-100" />
              </button>
              {!text.trim() && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleImagePick}
                    data-testid="chat-image-input"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={sending}
                    className="w-11 h-11 shrink-0 rounded-full bg-purple-500 text-white flex items-center justify-center hover:bg-purple-600 disabled:opacity-40 transition-colors"
                    title="שלח תמונה"
                    data-testid="chat-image-btn"
                  >
                    <ImagePlus className="w-5 h-5" />
                  </button>
                  <button
                    onClick={startRecording}
                    disabled={sending}
                    className="w-11 h-11 shrink-0 rounded-full bg-blue-500 text-white flex items-center justify-center hover:bg-blue-600 disabled:opacity-40 transition-colors"
                    title="הקלט הודעה קולית"
                    data-testid="chat-mic-btn"
                  >
                    <Mic className="w-5 h-5" />
                  </button>
                </>
              )}
            </>
          )}
        </div>

        {/* Image lightbox */}
        {lightboxImg && (
          <div
            className="fixed inset-0 z-[120] bg-black/90 flex items-center justify-center p-4 cursor-zoom-out"
            onClick={() => setLightboxImg(null)}
            data-testid="image-lightbox"
          >
            <img src={lightboxImg} alt="תמונה מוגדלת" className="max-w-full max-h-[85vh] object-contain rounded-xl" />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ChatConversation;
